import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import MainURL from '../../MainURL';

const CATEGORY_OPTIONS = ['예배', '교회학교', '행사·수련회', '봉사활동'];
const CATEGORY_DOT_COLORS = ['#1d4ed8', '#10b981', '#f59e0b', '#8b5cf6'];

type AlbumRow = {
  id: number;
  title: string;
  description: string;
  category: string;
  photoCount: number;
  pendingCount: number;
  coverImageUrl: string | null;
};

type PhotoRow = {
  id: number;
  imageUrl: string;
  caption: string;
  status: 'approved' | 'pending';
  uploaderName: string;
};

type PendingPhoto = {
  id: number;
  albumId: number;
  albumTitle: string;
  caption: string;
  uploaderName: string;
  imageUrl: string;
  createdAt: string;
};

const EMPTY_ALBUM_FORM = { title: '', description: '', category: CATEGORY_OPTIONS[0] };

async function compressImage(file: File): Promise<File> {
  try {
    return await imageCompression(file, {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 2000,
      useWebWorker: true,
    });
  } catch (err) {
    console.error('image compression failed, uploading original', err);
    return file;
  }
}

export default function ManageGallery({ authChurch }: { authChurch: string }) {
  const [albums, setAlbums] = useState<AlbumRow[]>([]);
  const [albumsLoading, setAlbumsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('전체');

  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingAlbumId, setEditingAlbumId] = useState<number | null>(null);
  const [albumForm, setAlbumForm] = useState(EMPTY_ALBUM_FORM);
  const [savingAlbum, setSavingAlbum] = useState(false);

  const [selectedAlbum, setSelectedAlbum] = useState<AlbumRow | null>(null);
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchAlbums = () => {
    setAlbumsLoading(true);
    axios
      .get(`${MainURL}/gallery/albums`, { params: { authChurch } })
      .then((res) => setAlbums(res.data?.data ?? []))
      .catch((err) => console.error('GET /gallery/albums', err))
      .finally(() => setAlbumsLoading(false));
  };

  const fetchPendingPhotos = () => {
    axios
      .get(`${MainURL}/gallery/photos/pending`, { params: { authChurch } })
      .then((res) => setPendingPhotos(res.data?.data ?? []))
      .catch((err) => console.error('GET /gallery/photos/pending', err));
  };

  useEffect(() => {
    fetchAlbums();
    fetchPendingPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch]);

  const fetchPhotos = (albumId: number) => {
    setPhotosLoading(true);
    axios
      .get(`${MainURL}/gallery/photos`, { params: { authChurch, albumId } })
      .then((res) => setPhotos(res.data?.data ?? []))
      .catch((err) => console.error('GET /gallery/photos', err))
      .finally(() => setPhotosLoading(false));
  };

  const openAlbum = (album: AlbumRow) => {
    setSelectedAlbum(album);
    fetchPhotos(album.id);
  };

  const backToAlbums = () => {
    setSelectedAlbum(null);
    setPhotos([]);
    fetchAlbums();
  };

  const openCreateAlbumForm = () => {
    setEditingAlbumId(null);
    setAlbumForm(EMPTY_ALBUM_FORM);
    setFormOpen(true);
  };

  const openEditAlbumForm = (album: AlbumRow, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAlbumId(album.id);
    setAlbumForm({ title: album.title, description: album.description, category: album.category || CATEGORY_OPTIONS[0] });
    setFormOpen(true);
  };

  const closeAlbumForm = () => {
    setFormOpen(false);
    setEditingAlbumId(null);
    setAlbumForm(EMPTY_ALBUM_FORM);
  };

  const submitAlbumForm = () => {
    if (!albumForm.title.trim()) return;
    setSavingAlbum(true);
    const request = editingAlbumId
      ? axios.put(`${MainURL}/gallery/albums/${editingAlbumId}`, { authChurch, ...albumForm })
      : axios.post(`${MainURL}/gallery/albums`, { authChurch, ...albumForm });

    request
      .then(() => {
        closeAlbumForm();
        fetchAlbums();
      })
      .catch((err) => console.error('save album', err))
      .finally(() => setSavingAlbum(false));
  };

  const removeAlbum = (album: AlbumRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`'${album.title}' 앨범과 안의 사진을 모두 삭제할까요?`)) return;
    axios
      .delete(`${MainURL}/gallery/albums/${album.id}`, { params: { authChurch } })
      .then(() => fetchAlbums())
      .catch((err) => console.error('DELETE /gallery/albums/:id', err));
  };

  const onFilesSelected = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !selectedAlbum) return;
    setUploading(true);
    try {
      for (const original of Array.from(fileList)) {
        const compressed = await compressImage(original);
        const formData = new FormData();
        formData.append('authChurch', authChurch);
        formData.append('albumId', String(selectedAlbum.id));
        formData.append('photo', compressed, original.name);
        await axios.post(`${MainURL}/gallery/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      fetchPhotos(selectedAlbum.id);
    } catch (err) {
      console.error('POST /gallery/photos', err);
      window.alert('일부 사진 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (photo: PhotoRow) => {
    if (!selectedAlbum) return;
    if (!window.confirm('이 사진을 삭제할까요?')) return;
    axios
      .delete(`${MainURL}/gallery/photos/${photo.id}`, { params: { authChurch } })
      .then(() => fetchPhotos(selectedAlbum.id))
      .catch((err) => console.error('DELETE /gallery/photos/:id', err));
  };

  const approvePhotoInAlbum = (photo: PhotoRow) => {
    if (!selectedAlbum) return;
    axios
      .put(`${MainURL}/gallery/photos/${photo.id}/approve`, { authChurch })
      .then(() => fetchPhotos(selectedAlbum.id))
      .catch((err) => console.error('PUT /gallery/photos/:id/approve', err));
  };

  const approvePending = (photo: PendingPhoto) => {
    axios
      .put(`${MainURL}/gallery/photos/${photo.id}/approve`, { authChurch })
      .then(() => {
        fetchPendingPhotos();
        fetchAlbums();
      })
      .catch((err) => console.error('PUT /gallery/photos/:id/approve', err));
  };

  const rejectPending = (photo: PendingPhoto) => {
    if (!window.confirm('이 사진을 반려(삭제)할까요?')) return;
    axios
      .delete(`${MainURL}/gallery/photos/${photo.id}`, { params: { authChurch } })
      .then(() => {
        fetchPendingPhotos();
        fetchAlbums();
      })
      .catch((err) => console.error('DELETE /gallery/photos/:id', err));
  };

  if (selectedAlbum) {
    return (
      <div className="manage-panel">
        <div className="manage-panel-header">
          <div className="manage-gallery-album-heading">
            <button type="button" className="manage-btn manage-btn--sm" onClick={backToAlbums}>← 앨범 목록</button>
            <h2 className="manage-panel-title">{selectedAlbum.title}</h2>
          </div>
          <button
            type="button"
            className="manage-btn manage-btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? '업로드 중...' : '+ 사진 추가'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => onFilesSelected(e.target.files)}
          />
        </div>

        <section className="manage-card-block">
          <div className="manage-block-head">
            <h3 className="manage-block-title">사진</h3>
            <span className="manage-block-count">{photos.length}</span>
          </div>
          {photosLoading && <div className="manage-state">불러오는 중입니다...</div>}
          {!photosLoading && (
            <div className="manage-gallery-grid">
              {photos.length === 0 && (
                <div className="manage-table-empty">등록된 사진이 없습니다.</div>
              )}
              {photos.map((photo) => (
                <div className="manage-gallery-photo" key={photo.id}>
                  <img src={`${MainURL}${photo.imageUrl}`} alt={photo.caption || selectedAlbum.title} />
                  {photo.status === 'pending' && (
                    <span className="manage-badge manage-badge--amber manage-gallery-photo-pending">승인대기</span>
                  )}
                  {photo.status === 'pending' && (
                    <button type="button" className="manage-gallery-photo-approve" onClick={() => approvePhotoInAlbum(photo)}>✓ 승인</button>
                  )}
                  <button type="button" className="manage-gallery-photo-remove" onClick={() => removePhoto(photo)}>×</button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  }

  const filteredAlbums = activeCategory === '전체' ? albums : albums.filter((a) => a.category === activeCategory);
  const totalPhotoCount = albums.reduce((sum, a) => sum + a.photoCount, 0);
  const categoryBreakdown = CATEGORY_OPTIONS.map((category, index) => ({
    category,
    count: albums.filter((a) => a.category === category).length,
    color: CATEGORY_DOT_COLORS[index % CATEGORY_DOT_COLORS.length],
  }));

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">갤러리</h2>
          <p className="manage-panel-desc">앱 갤러리에 노출되는 앨범과 사진을 관리합니다. 성도들이 올린 사진은 승인 후 게시됩니다.</p>
        </div>
        <button type="button" className="manage-btn manage-btn--primary" onClick={openCreateAlbumForm}>
          + 새 앨범
        </button>
      </div>

      {!albumsLoading && albums.length > 0 && (
        <div className="manage-stats">
          <div className="manage-stat-card">
            <span>전체 앨범</span>
            <strong>{albums.length}개</strong>
          </div>
          <div className="manage-stat-card">
            <span>전체 사진</span>
            <strong>{totalPhotoCount.toLocaleString('ko-KR')}장</strong>
          </div>
          <div className="manage-stat-card">
            <span>승인 대기 사진</span>
            <strong>{pendingPhotos.length}장</strong>
          </div>
        </div>
      )}

      {!albumsLoading && albums.length > 0 && (
        <div className="manage-filterbar">
          <div className="manage-seg">
            {['전체', ...CATEGORY_OPTIONS].map((category) => (
              <button
                key={category}
                type="button"
                className={`manage-seg-btn${activeCategory === category ? ' is-active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="manage-grid-2col">
        <div>
          <section className="manage-card-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">앨범 목록</h3>
              <span className="manage-block-count">{filteredAlbums.length}</span>
            </div>
            {albumsLoading && <div className="manage-state">불러오는 중입니다...</div>}
            {!albumsLoading && (
              <div className="manage-gallery-grid">
                {filteredAlbums.length === 0 && (
                  <div className="manage-table-empty">등록된 앨범이 없습니다.</div>
                )}
                {filteredAlbums.map((album, index) => (
                  <div className="manage-gallery-album-card" key={album.id} onClick={() => openAlbum(album)}>
                    <div className={`manage-gallery-album-cover manage-gallery-album-cover--${(index % 6) + 1}`}>
                      {album.coverImageUrl ? (
                        <img src={`${MainURL}${album.coverImageUrl}`} alt={album.title} />
                      ) : (
                        <span className="manage-gallery-album-cover-empty">📷</span>
                      )}
                      <span className="manage-gallery-photo-count">📷 {album.photoCount}장</span>
                      {album.pendingCount > 0 && (
                        <span className="manage-badge manage-badge--amber manage-gallery-album-pending">승인대기 {album.pendingCount}</span>
                      )}
                    </div>
                    <div className="manage-gallery-album-info">
                      <p className="manage-gallery-album-title">{album.title}</p>
                      <p className="manage-gallery-album-meta">
                        {album.category ? `${album.category} · ` : ''}사진 {album.photoCount}장
                      </p>
                    </div>
                    <div className="manage-row-actions">
                      <button type="button" className="manage-btn manage-btn--sm" onClick={(e) => openEditAlbumForm(album, e)}>수정</button>
                      <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={(e) => removeAlbum(album, e)}>삭제</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {pendingPhotos.length > 0 && (
            <section className="manage-card-block">
              <div className="manage-block-head">
                <h3 className="manage-block-title">성도 업로드 승인 대기</h3>
                <span className="manage-block-count manage-block-count--amber">{pendingPhotos.length}</span>
              </div>
              <div className="manage-gallery-pending-strip">
                {pendingPhotos.map((photo) => (
                  <div className="manage-gallery-pending-card" key={photo.id}>
                    <div className="manage-gallery-photo">
                      <img src={`${MainURL}${photo.imageUrl}`} alt={photo.caption} />
                    </div>
                    <p className="manage-gallery-album-meta">{photo.albumTitle}</p>
                    <p className="manage-gallery-album-meta">{photo.uploaderName || '작성자 미상'}</p>
                    <div className="manage-row-actions">
                      <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" onClick={() => approvePending(photo)}>승인</button>
                      <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={() => rejectPending(photo)}>반려</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="manage-side-stack">
          <div className="manage-side-card manage-worship-phone">
            <div className="manage-worship-phone-notch">
              갤러리
              <small>앱 갤러리 탭 미리보기</small>
            </div>
            <div className="manage-worship-phone-screen">
              {albums.length === 0 && <div className="manage-side-empty">등록된 앨범이 없습니다.</div>}
              {albums.slice(0, 5).map((album) => (
                <div className="manage-worship-prow" key={album.id}>
                  <b>{album.title}</b>
                  <span>{album.photoCount}장</span>
                </div>
              ))}
            </div>
          </div>

          <div className="manage-side-card">
            <div className="manage-side-card-head">🗂 행사 구분별 앨범</div>
            <div className="manage-side-card-body">
              {albums.length === 0 && <p className="manage-side-empty">등록된 앨범이 없습니다.</p>}
              {albums.length > 0 && categoryBreakdown.map((row) => (
                <div className="manage-stat-row" key={row.category}>
                  <span className="manage-stat-row-dot" style={{ background: row.color }} />
                  <span className="manage-stat-row-label">{row.category}</span>
                  <span className="manage-stat-row-count">{row.count}개</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {formOpen && (
        <div className="manage-modal-overlay" onClick={closeAlbumForm}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editingAlbumId ? '앨범 수정' : '새 앨범'}</h3>
              <button type="button" className="manage-modal-close" onClick={closeAlbumForm}>×</button>
            </div>
            <div className="manage-modal-body">
              <div className="manage-form">
                <label className="manage-field">
                  <span>앨범 제목</span>
                  <input value={albumForm.title} onChange={(e) => setAlbumForm({ ...albumForm, title: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>행사 구분</span>
                  <select value={albumForm.category} onChange={(e) => setAlbumForm({ ...albumForm, category: e.target.value })}>
                    {CATEGORY_OPTIONS.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>설명</span>
                  <textarea rows={2} value={albumForm.description} onChange={(e) => setAlbumForm({ ...albumForm, description: e.target.value })} />
                </label>
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={closeAlbumForm}>취소</button>
              <button
                type="button"
                className="manage-btn manage-btn--primary"
                onClick={submitAlbumForm}
                disabled={savingAlbum || !albumForm.title.trim()}
              >
                {savingAlbum ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
